/**
 * Site identity helpers.
 *
 * In dev, the four bench-served sites all live on `localhost` (or any alias of
 * the same machine) and are distinguished by PORT, not hostname:
 *   8022 → main         (amazonconnector.local)
 *   8023 → na.region    (na.amazonconnector.local)
 *   8024 → eu.region    (eu.amazonconnector.local)
 *   8025 → fe.region    (fe.amazonconnector.local)
 *
 * Hostname-based detection breaks the moment someone visits via `localhost`,
 * `127.0.0.1`, or any other alias. Trust port first; fall back to hostname
 * for production/port-less URLs.
 *
 * Single-region mode (server-controlled via `single_region_mode` in
 * site_config.json): collapses main + region into one deployment. Login /
 * signup pages live on the region site itself, no cross-domain redirect.
 * Local dev typically leaves it OFF so the multi-region flow stays testable.
 */

const MAIN_SITE_PORT = '8022';
const REGION_PORTS = new Set(['8023', '8024', '8025']);

// Initial state of the single-region flag.
//   - On a hard refresh we may render the first frame BEFORE the server
//     /site_mode fetch resolves. Without a cached hint, the UI would default
//     to multi-region and momentarily show wrong routes / region picker.
//   - Persist the last-seen value in localStorage so subsequent loads pick the
//     right path synchronously. First-ever load on a new browser still sees
//     the multi-region default for one tick, which is fine — the fetch lands
//     quickly and the auth guard re-resolves.
const STORAGE_KEY = 'mc.single_region_mode';
let _singleRegion = (() => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
})();
let _modeFetched = false;

export function isSingleRegion() {
  return _singleRegion;
}

/**
 * Fetch the server's single_region_mode flag and cache it. main.js awaits
 * this before mounting so first-frame routing matches the deployment.
 */
export async function loadSiteMode() {
  if (_modeFetched) return _singleRegion;
  _modeFetched = true;
  try {
    const resp = await fetch(
      `${window.location.protocol}//${window.location.host}` +
      '/api/method/marketplace_connector.api.auth.site_mode',
      { credentials: 'include' },
    );
    if (resp.ok) {
      const body = await resp.json();
      _singleRegion = !!(body?.message?.single_region);
      try {
        window.localStorage.setItem(STORAGE_KEY, String(_singleRegion));
      } catch {
        // Storage blocked (private mode?) — fine, fall back to network each load.
      }
    }
  } catch {
    // Network blip — keep whatever the cache had. Auth flows still work.
  }
  return _singleRegion;
}

export function isMainSite() {
  // Single-region deployments have no concept of a "main" site — every
  // visit is to the one site that handles auth + portal.
  if (_singleRegion) return false;

  const port = window.location.port;
  if (port) {
    if (port === MAIN_SITE_PORT) return true;
    if (REGION_PORTS.has(port)) return false;
  }
  // Production / port-less: bare host = main, subdomain = region.
  const host = window.location.hostname;
  return host === 'amazonconnector.local' || host === 'amazonconnector.com';
}

export function mainSiteUrl() {
  // Single-region: there is no separate main site to redirect to. Return the
  // current origin so any leftover cross-site redirect just stays in-app.
  if (_singleRegion) {
    return `${window.location.protocol}//${window.location.host}`;
  }

  const proto = window.location.protocol;
  const host = window.location.hostname;
  const port = window.location.port;

  // Dev: same hostname (localhost / 127.0.0.1 / *.amazonconnector.local),
  // just swap port to 8022.
  if (port && (port === MAIN_SITE_PORT || REGION_PORTS.has(port))) {
    return `${proto}//${host}:${MAIN_SITE_PORT}`;
  }
  // Prod: strip subdomain prefix to get bare main host.
  const bareHost = host.replace(/^(na|eu|fe)\./, '');
  return `${proto}//${bareHost}`;
}
